import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { useRef, useState } from "react";
import { requests } from "../../services/http-common";
import classes from "./ContractImportExport.module.scss";
import { classNames as cx } from "primereact/utils";
import { useToast } from "../../Providers/ToastProvider";
import { useUser } from "../../keycloak";
import { UserRoles } from "../../constants/userRoles";

type ContractImportExportProps = {
  exportUrl?: string;
  importUrl: string;
  refetchQuery: () => void;
  showExport?: boolean;
};

export const ContractImportExport = ({
  exportUrl,
  importUrl,
  refetchQuery,
  showExport = false,
}: ContractImportExportProps) => {
  const { show } = useToast();
  const menu = useRef<any>(null);
  const importDataInputRef = useRef<any>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState<
    "idle" | "uploading" | "processing" | "success" | "error"
  >("idle");
  const [importMessage, setImportMessage] = useState("");

  const user: any = useUser();

  const exportData = useQuery({
    queryKey: [`export-data-${exportUrl}`],
    queryFn: () => {
      return requests.post(exportUrl || "", {});
    },
    onSuccess: (resp) => {
      const link = document.createElement("a");
      link.href = `${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${resp}`;
      link.setAttribute("download", `contracts_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode && link.parentNode.removeChild(link);
      show({ detail: "Export started successfully!" });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail:
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          "Export failed",
      });
    },
    retry: false,
    enabled: false,
  });

  const downloadTemplate = () => {
    const csvContent = `contractTypeName,leadTitle,clientEmail,clientName,companyName,address,phone,amount,compensation,cancellation,status,estimatedSQFT,typeOfSpace,pets,recurrence,frequency,recurEvery,daysOfWeek,scopeOfWorks,supplies,paperProducts,typeOfFloorSurfaces,quickbooksCustomerId,createdDate,skipQuickbooksSync
Professional Cleaning Services Proposal,CEO,john@acmecorp.com,John Doe,Acme Corporation,123 Main St Denver CO 80202,(303) 555-1234,1500.00,$1500.00 per month,100,Sent,5000,Office,N,Y,Weekly,1,"[""Monday"",""Wednesday"",""Friday""]",Dusting;Vacuuming;Mopping;Trash Removal,All Purpose Cleaner;Glass Cleaner,Paper Towels;Toilet Paper,Hardwood;Tile,QB123,2024-01-15,false
Professional Maid Proposal,COO,jane@smithhome.com,Jane Smith,Smith Residence,456 Oak Ave Denver CO 80203,(303) 555-5678,250.00,$250.00 per visit,50,Sent,2500,Residential,Y,Y,Bi-Weekly,2,"[""Tuesday""]",Dusting;Vacuuming;Kitchen Cleaning;Bathroom Cleaning,All Purpose Cleaner;Disinfectant,Paper Towels,Carpet;Hardwood,QB456,2024-02-01,false`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "contract_import_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    show({ detail: "Template downloaded successfully!" });
  };

  const importData = useMutation({
    mutationFn: (file: File) => {
      setShowImportDialog(true);
      setImportStatus("uploading");
      setImportProgress(30);
      setImportMessage("Uploading CSV file...");

      const formData = new FormData();
      formData.append("file", file);
      return requests.post(importUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        transformRequest: (formData: any) => formData,
      });
    },
    onSuccess: (response: any) => {
      setImportProgress(100);
      setImportStatus("success");

      if (response.failedCsvPath) {
        setImportMessage(
          `Import completed with some errors. ${response.message || ""}`,
        );
        show({
          severity: "warn",
          detail:
            "Some contracts failed to import. Download the failed records for details.",
          life: 5000,
        });

        // Offer to download failed records
        setTimeout(() => {
          const link = document.createElement("a");
          link.href = `${process.env.REACT_APP_DSC_SERVICE_DOMAIN}${response.failedCsvPath}`;
          link.setAttribute("download", `failed_contracts.csv`);
          document.body.appendChild(link);
          link.click();
          link.parentNode && link.parentNode.removeChild(link);
        }, 1000);
      } else {
        setImportMessage("All contracts imported successfully!");
        show({
          severity: "success",
          detail: "All contracts imported successfully!",
        });
      }

      refetchQuery();
      setTimeout(() => {
        setShowImportDialog(false);
        resetImportState();
      }, 2000);
    },
    onError: (error: any) => {
      setImportProgress(0);
      setImportStatus("error");
      const errorMessage =
        error.response?.data?.message || error.message || "Import failed";
      setImportMessage(`Error: ${errorMessage}`);
      show({
        severity: "error",
        detail: errorMessage,
        life: 5000,
      });
    },
  });

  const resetImportState = () => {
    setImportProgress(0);
    setImportStatus("idle");
    setImportMessage("");
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file type
      if (!file.name.endsWith(".csv")) {
        show({
          severity: "error",
          detail: "Please select a CSV file",
        });
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        show({
          severity: "error",
          detail: "File size must be less than 10MB",
        });
        return;
      }

      importData.mutate(file);
    }

    // Reset input value so same file can be selected again
    e.target.value = "";
  };

  const items = [
    ...(showExport &&
      exportUrl &&
      user?.roles.dcs_client.includes(UserRoles.CONTRACT_VIEW)
      ? [
        {
          label: "Export Contracts CSV",
          icon: "pi pi-download",
          command: () => {
            exportData.refetch();
          },
        },
      ]
      : []),
    ...(user?.roles.dcs_client.includes(UserRoles.CONTRACT_CREATE)
      ? [
        {
          label: "Download Import Template",
          icon: "pi pi-file",
          command: () => {
            downloadTemplate();
          },
        },
        {
          label: "Import Contracts CSV",
          icon: "pi pi-upload",
          command: () => {
            importDataInputRef.current.click();
          },
        },
      ]
      : []),
  ];

  const getProgressColor = () => {
    switch (importStatus) {
      case "success":
        return "#22c55e";
      case "error":
        return "#ef4444";
      default:
        return "#3b82f6";
    }
  };

  const dialogFooter = (
    <div className={classes.dialogFooter}>
      {importStatus === "success" || importStatus === "error" ? (
        <Button
          label="Close"
          onClick={() => {
            setShowImportDialog(false);
            resetImportState();
          }}
          className="p-button-text"
        />
      ) : null}
    </div>
  );

  return (
    <>
      <div className={classes.content}>
        <Button
          icon="pi pi-file-import"
          label="Import"
          className={cx(
            "p-button-outlined p-button-secondary",
            classes.importButton,
          )}
          onClick={(event) => menu.current && menu.current.toggle(event)}
          tooltip="Import/Export Contracts"
          tooltipOptions={{ position: "bottom" }}
        />
        {items.length ? (
          <Menu
            model={items}
            popup
            ref={menu}
            id="contract-import-export-menu"
          />
        ) : null}
        <input
          type="file"
          ref={importDataInputRef}
          className={classes.importDataInputRef}
          accept=".csv"
          onChange={handleFileSelect}
        />
      </div>

      <Dialog
        header="Importing Contracts"
        visible={showImportDialog}
        style={{ width: "500px" }}
        onHide={() => {
          if (importStatus !== "uploading" && importStatus !== "processing") {
            setShowImportDialog(false);
            resetImportState();
          }
        }}
        footer={dialogFooter}
        closable={importStatus !== "uploading" && importStatus !== "processing"}
      >
        <div className={classes.importDialog}>
          <div className={classes.importIcon}>
            {importStatus === "success" && (
              <i
                className="pi pi-check-circle"
                style={{ fontSize: "3rem", color: "#22c55e" }}
              />
            )}
            {importStatus === "error" && (
              <i
                className="pi pi-times-circle"
                style={{ fontSize: "3rem", color: "#ef4444" }}
              />
            )}
            {(importStatus === "uploading" ||
              importStatus === "processing") && (
                <i
                  className="pi pi-spin pi-spinner"
                  style={{ fontSize: "3rem", color: "#3b82f6" }}
                />
              )}
          </div>

          <div className={classes.importMessage}>
            {importMessage || "Processing your file..."}
          </div>

          {importProgress > 0 && (
            <ProgressBar
              value={importProgress}
              showValue={false}
              style={{ height: "6px", marginTop: "20px" }}
              color={getProgressColor()}
            />
          )}

          {importStatus === "success" && (
            <div className={classes.successInfo}>
              <p>
                <i
                  className="pi pi-info-circle"
                  style={{ marginRight: "8px" }}
                />
                Contracts have been imported and are now available in the list.
              </p>
            </div>
          )}

          {importStatus === "error" && (
            <div className={classes.errorInfo}>
              <p>
                <i
                  className="pi pi-exclamation-triangle"
                  style={{ marginRight: "8px" }}
                />
                Please check your CSV file and try again.
              </p>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};
